import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toSafeMarkdownHtml } from '@/core/utils/toSafeMarkdownHtml';
import { getDossierUrl } from '@/core/utils/getDossierUrl';
import { MOCK_POSTS } from '@/core/services/interfaces/PostMockData';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { CardPostsLocale } from '@/features/publication-wall/constants/labels';
import { BdsAvatar, BdsIcon, BdsTooltip } from '../../../../core/constants/webComponents';
import './FullPostModal.scss';
import { useChannelIcon } from '@/core/hooks/useChannelIcon';
import { CARD_POST_EVENTS } from '@/features/publication-wall/constants/events';
import { initI18n } from '@/core/config/i18n';
import { sanitizeDisplayText } from '@/core/utils/sanitizeDisplayText';
import { useValidatedThumbnail } from '@/core/hooks/useValidatedThumbnail';
import { MediaRenderer } from '@/core/components/MediaRenderer/MediaRenderer';
import { resolveImageUrl, resolveVideoUrl, resolveVideoMimeType, resolveEmbedUrl, resolveAudioUrl } from '@/core/utils/resolvePostMediaUrl';
import { PlatformIconLink } from '@/core/components/PlatformIconLink/PlatformIconLink';
import { getPostLanguageDisplayName } from '@/core/utils/postLanguage';
import { parseImageText } from '@/core/utils/parseImageText';

interface FullPostModalProps {
  post?: Post;
  locale?: CardPostsLocale;
  onClose: () => void;
}

const isModalLocale = (value?: string): value is CardPostsLocale => {
  return value === 'pt' || value === 'es' || value === 'en';
};



const useModalEvents = (post: Post | null, t: (key: string) => string, onClose: () => void) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) dialog.showModal();

    const handleClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        document.dispatchEvent(
          new CustomEvent(CARD_POST_EVENTS.FULL_POST_MODAL_CLICK, {
            detail: { postId: post?._id, action: 'close-backdrop', value: t('cardPosts.close') },
            bubbles: true,
            composed: true,
          }),
        );
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        document.dispatchEvent(
          new CustomEvent(CARD_POST_EVENTS.FULL_POST_MODAL_CLICK, {
            detail: { postId: post?._id, action: 'close-escape', value: t('cardPosts.close') },
            bubbles: true,
            composed: true,
          }),
        );
        onClose();
      }
    };

    dialog.addEventListener('click', handleClick);
    dialog.addEventListener('keydown', handleKeyDown);

    return () => {
      dialog.removeEventListener('click', handleClick);
      dialog.removeEventListener('keydown', handleKeyDown);
    };
  }, [post, t, onClose]);

  useEffect(() => {
    document.body.classList.add('full-post-modal-open');
    return () => {
      document.body.classList.remove('full-post-modal-open');
    };
  }, []);

  return dialogRef;
};

const AuthorSection: React.FC<{
  post: Post;
  platform: string;
  channelData: any;
  dossierURL: string;
  handleDossierClick: () => void;
  t: (key: string) => string;
  avatarThumbnail: string | undefined;
  publicationUrl: string;
  publicationTooltip: string;
  postLanguage: string | null;
  onNavigate: () => void;
}> = ({ post, platform, channelData, dossierURL, handleDossierClick, t, avatarThumbnail, publicationUrl, publicationTooltip, postLanguage, onNavigate }) => (
  <div className="full-post-modal__author">
    <PlatformIconLink
      publicationUrl={publicationUrl}
      tooltipText={publicationTooltip}
      channelIconUrl={channelData?.icons?.default}
      platform={platform}
      wrapperClassName="full-post-modal__platform"
      linkClassName="full-post-modal__platform-link"
      buttonClassName="full-post-modal__platform-icon"
      tooltipPosition="top-left"
      onNavigate={onNavigate}
    />
    {dossierURL ? (
      <button
        className="full-post-modal__avatar full-post-modal__avatar--clickable"
        onClick={handleDossierClick}
        aria-label={t('cardPosts.view')}
        title={t('cardPosts.view')}
      >
        <BdsAvatar
          class={`post-card__avatar-wrapper${dossierURL ? ' post-card__avatar-wrapper--clickable' : ''}`}
          size="extra-small"
          name={sanitizeDisplayText(post.name) ?? post.name}
          thumbnail={avatarThumbnail}
          onClick={handleDossierClick}
        />
      </button>
    ) : (
      <div className="full-post-modal__avatar">
        <BdsAvatar
          class={`post-card__avatar-wrapper${dossierURL ? ' post-card__avatar-wrapper--clickable' : ''}`}
          size="extra-small"
          name={sanitizeDisplayText(post.name) ?? post.name}
          thumbnail={avatarThumbnail}
          onClick={handleDossierClick}
        />
      </div>
    )}
    <div className="full-post-modal__author-info">
      <div className="full-post-modal__author-name-wrapper">
        {dossierURL ? (
          <button
            className="full-post-modal__author-name full-post-modal__author-name--clickable"
            onClick={handleDossierClick}
            aria-label={t('cardPosts.view')}
            title={t('cardPosts.view')}
          >
            {post.name}
          </button>
        ) : (
          <p className="full-post-modal__author-name">
            {post.name}
          </p>
        )}
        {post.verified && (
          <BdsTooltip position="top-center" tooltip-text={t('cardPosts.verifiedTooltipInfo')}>
            <span className="full-post-modal__verified-icon">
              <BdsIcon name="checkball" theme="solid" size="small" />
            </span>
          </BdsTooltip>
        )}
      </div>
      <p className="full-post-modal__date">{post.posted_at}</p>
      {postLanguage && <p className="full-post-modal__language">{t('cardPosts.language')} {postLanguage}</p>}
    </div>
  </div>
);

export const FullPostModal: React.FC<FullPostModalProps> = ({ post: postProp, onClose, locale }) => {
  const resolvedLocale = isModalLocale(locale) ? locale : 'pt';
  const { t } = useTranslation();
  const isDevelopment = process.env.NODE_ENV === 'development';
  const post = postProp ?? (isDevelopment ? MOCK_POSTS[0] : null);
  const platform = (post?.primary_channel ?? post?.channel ?? '').toLowerCase();
  const authorName = post?.name ?? '';
  const publicationUrl = post?.post_url;
  const imageUrl = post ? resolveImageUrl(post) : undefined;
  const videoUrl = post ? resolveVideoUrl(post) : undefined;
  const videoMimeType = post ? resolveVideoMimeType(post) : undefined;
  const videoPreload = !imageUrl && videoUrl ? 'metadata' : 'none';
  const embedUrl = post ? resolveEmbedUrl(post) : undefined;
  const audioUrl = post ? resolveAudioUrl(post) : undefined;
  const [hasMedia, setHasMedia] = useState<boolean>(Boolean(embedUrl ?? videoUrl ?? audioUrl ?? imageUrl));
  const channelData = useChannelIcon(platform);
  const hasTitle = Boolean(post?.title?.trim());
  const renderedTitleHtml = hasTitle ? toSafeMarkdownHtml(post!.title!.trim()) : '';
  const renderedTextHtml = toSafeMarkdownHtml(post?.text ?? '');
  const imageTextHtml = parseImageText(post?.computer_vision, t('cardPosts.computerVisionHeader'));
  const socialNetworkName = (post?.channel ?? post?.primary_channel ?? platform)
    .trim()
    .replaceAll('_', ' ')
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
  const publicationTooltip = t('cardPosts.viewPublicationTooltip', { socialNetworkName });
  const postLanguage = getPostLanguageDisplayName(post?.lang, t);
  const dossierURL = useMemo(() => getDossierUrl(post?.userData), [post?.userData]);
  const dialogRef = useModalEvents(post, t, onClose);
  const avatarThumbnail = useValidatedThumbnail(post?.post_user_image_url);

  initI18n(resolvedLocale);

  const handlePublicationLinkClick = () => {
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.FULL_POST_MODAL_CLICK, {
        detail: { postId: post?._id, action: 'open-publication-link', value: publicationUrl },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const handleDossierClick = () => {
    if (dossierURL) {
      window.open(dossierURL, '_blank', 'noopener,noreferrer');
    }
  };

  if (!post) return null;

  const handleCloseButtonClick = () => {
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.FULL_POST_MODAL_CLICK, {
        detail: { postId: post._id, action: 'close-button', value: t('cardPosts.close') },
        bubbles: true,
        composed: true,
      }),
    );
    onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className={`full-post-modal__overlay ${hasMedia ? 'full-post-modal__overlay--with-media' : 'full-post-modal__overlay--text-only'}`}
      aria-label={t('cardPosts.fullPostTitle')}
      onClose={onClose}
    >
      <div className="full-post-modal__container">
        <button
          className="full-post-modal__close"
          onClick={handleCloseButtonClick}
          aria-label={t('cardPosts.close')}
          title={t('cardPosts.close')}
        >
          <BdsIcon theme="outline" name="close" size="small" />
        </button>

        <div className="full-post-modal__content">
          <MediaRenderer
            videoUrl={videoUrl}
            videoMimeType={videoMimeType}
            embedUrl={embedUrl}
            imageUrl={imageUrl}
            audioUrl={audioUrl}
            alt={authorName}
            wrapperClassName="full-post-modal__image"
            videoPreload={videoPreload}
            audioPreload="metadata"
            videoPoster={imageUrl}
            videoTimeoutMs={8000}
            onHasMediaChange={setHasMedia}
          />

          <div className="full-post-modal__body">
            <AuthorSection
              post={post}
              platform={platform}
              channelData={channelData}
              dossierURL={dossierURL}
              handleDossierClick={handleDossierClick}
              t={t}
              avatarThumbnail={avatarThumbnail}
              publicationUrl={publicationUrl ?? ''}
              publicationTooltip={publicationTooltip}
              postLanguage={postLanguage}
              onNavigate={handlePublicationLinkClick}
            />

            {hasTitle && (
              <p className="full-post-modal__title" dangerouslySetInnerHTML={{ __html: renderedTitleHtml }}></p>
            )}
            <p className={`full-post-modal__text ${hasMedia ? 'full-post-modal__text--with-media' : ''}`} dangerouslySetInnerHTML={{ __html: renderedTextHtml }}></p>
            {imageTextHtml && (
              <div className="full-post-modal__image-text" dangerouslySetInnerHTML={{ __html: imageTextHtml }} />
            )}
            <div className="full-post-modal__hashtags">
              {post.hashtags?.map((tag) => (
                <span key={tag} className="full-post-modal__hashtag">{tag}</span>
              ))}
            </div>
            {publicationUrl && (
              <a
                className="full-post-modal__publication-link"
                href={publicationUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={publicationUrl}
                onClick={handlePublicationLinkClick}
              >
                {publicationUrl}
              </a>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
};
